import React, { useEffect, useState } from "react";
import {
  Pane,
  TextInput,
  Select,
  Button,
  FilePicker,
  toaster,
  Table,
  Dialog,
} from "evergreen-ui";
import "./AdminCarHire.css";

const CarHireEdit = () => {
  const [cars, setCars] = useState([]);
  const [selectedCarId, setSelectedCarId] = useState(null);
  const [form, setForm] = useState(initialForm());
  const [isDialogShown, setIsDialogShown] = useState(false);

  function initialForm() {
    return {
      car_name: "",
      price: "",
      image: null,
      seats: "",
      bags: "",
      transmission: "automatic",
      fuel: "petrol",
      air_conditioning: true,
      airbags: "",
      reverse_camera: false,
      lane_assist: false,
      gps: false,
      usb: false,
      bluetooth: false,
      touchscreen: false,
      mileage: "",
    };
  }

  useEffect(() => {
    fetchCars();
  }, []);

  const fetchCars = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BASE_URL}/carhires`);
      const data = await res.json();
      setCars(data);
    } catch (error) {
      toaster.danger("Failed to fetch cars");
    }
  };

  const handleSelectCar = (car) => {
    setSelectedCarId(car.id);
    setForm({ ...car });
    setIsDialogShown(true);
  };

  const handleFileChange = (files) => {
    if (files && files[0]) {
      setForm({ ...form, image: files[0] });
    }
  };

  const handleSave = async () => {
    const formData = new FormData();

    Object.keys(form).forEach((key) => {
      if (key === "image" && form[key] instanceof File) {
        formData.append(key, form[key]);
      } else {
        formData.append(key, form[key]);
      }
    });

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/carhires/${selectedCarId}`,
        {
          method: "PUT",
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update car");
      }

      toaster.success("Car updated successfully!");
      setIsDialogShown(false);
      fetchCars();
    } catch (error) {
      toaster.danger(error.message);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/carhires/${selectedCarId}`,
        {
          method: "DELETE",
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to delete car");
      }

      toaster.success("Car deleted successfully!");
      setIsDialogShown(false);
      setForm(initialForm());
      setSelectedCarId(null);
      fetchCars();
    } catch (error) {
      toaster.danger(error.message);
    }
  };

  return (
    <Pane className="form-container">
      <h2>Edit Car Hire Listings</h2>

      <Table>
        <Table.Head>
          <Table.TextHeaderCell>Car Name</Table.TextHeaderCell>
          <Table.TextHeaderCell>Price</Table.TextHeaderCell>
          <Table.TextHeaderCell>Edit</Table.TextHeaderCell>
        </Table.Head>
        <Table.Body>
          {cars.map((car) => (
            <Table.Row key={car.id}>
              <Table.TextCell>{car.car_name}</Table.TextCell>
              <Table.TextCell>${car.price}</Table.TextCell>
              <Table.Cell>
                <Button onClick={() => handleSelectCar(car)}>Edit</Button>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      {/* Dialog box for editing */}
      <Dialog
        isShown={isDialogShown}
        title="Edit Car Details"
        onCloseComplete={() => setIsDialogShown(false)}
        confirmLabel="Save"
        onConfirm={handleSave}
        cancelLabel="Cancel"
        hasFooter={false}
        width={600}
      >
        <Pane display="flex" flexDirection="column" gap={12}>
          <TextInput
            placeholder="Car Name"
            value={form.car_name}
            onChange={(e) => setForm({ ...form, car_name: e.target.value })}
          />
          <TextInput
            type="number"
            placeholder="Price"
            value={form.price}
            onChange={(e) => setForm({ ...form, price: e.target.value })}
          />
          <FilePicker
            width="100%"
            onChange={handleFileChange}
            placeholder="Replace image"
            accept="image/*"
          />
          <Select
            value={form.transmission}
            onChange={(e) =>
              setForm({ ...form, transmission: e.target.value })
            }
          >
            <option value="automatic">Automatic</option>
            <option value="manual">Manual</option>
          </Select>
          <Select
            value={form.fuel}
            onChange={(e) => setForm({ ...form, fuel: e.target.value })}
          >
            <option value="petrol">Petrol</option>
            <option value="diesel">Diesel</option>
            <option value="electric">Electric</option>
            <option value="hybrid">Hybrid</option>
          </Select>
          <TextInput
            type="number"
            placeholder="Seats"
            value={form.seats}
            onChange={(e) => setForm({ ...form, seats: e.target.value })}
          />
          <TextInput
            type="number"
            placeholder="Luggage (Bags)"
            value={form.bags}
            onChange={(e) => setForm({ ...form, bags: e.target.value })}
          />
          <TextInput
            placeholder="Airbags"
            value={form.airbags}
            onChange={(e) => setForm({ ...form, airbags: e.target.value })}
          />
          <TextInput
            type="number"
            placeholder="Mileage"
            value={form.mileage}
            onChange={(e) => setForm({ ...form, mileage: e.target.value })}
          />

          <Pane display="flex" justifyContent="space-between" marginTop={16}>
            <Button appearance="danger" onClick={handleDelete}>
              Delete Car
            </Button>
            <Button appearance="primary" onClick={handleSave}>
              Save Changes
            </Button>
          </Pane>
        </Pane>
      </Dialog>
    </Pane>
  );
};

export default CarHireEdit;
